import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Spin, DatePicker, Select, message } from 'antd';
import moment from 'moment';
import { getOpenAccountsReport, getAllWasamsByBranchIds } from './SavingService';
import { exportOpenAccountsExcelReport } from '../../Utils/OpenAccountExcelExport';

const { RangePicker } = DatePicker;
const { Option, OptGroup } = Select;
const OpenAccountReport = ({
    products = [],
    loginPlaceId,
    districtData = [],
    districtName,
    divisionalId,
    branchId,
    branchName,
    wasamData = [],
}) => {

    const [loading, setLoading]          = useState(false);
    const [startDate, setStartDate]      = useState(null);
    const [endDate, setEndDate]          = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);

    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedWasam, setSelectedWasam] = useState([]);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [branchWasams, setBranchWasams] = useState([]);

    const wasamOptions = loginPlaceId === "1" ? wasamData : branchWasams;

    useEffect(() => {
        let filteredDivisionalAreas = [];

        if (loginPlaceId === "2" && divisionalId) {
            filteredDivisionalAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === divisionalId);

            setSelectedDivisionalAreas([divisionalId]);
        } else {
            filteredDivisionalAreas = districtData
                .filter(d => selectedDistricts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);

            setSelectedDivisionalAreas(prev =>
                prev.filter(did =>
                    filteredDivisionalAreas.some(d => d.divAId === did)
                )
            );
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [selectedDistricts, districtData, loginPlaceId, divisionalId]);

    useEffect(() => {
        const filteredBranches = districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => selectedDivisionalAreas.includes(div.divAId))
            .flatMap(div => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);

        setSelectedBranches(prev =>
            prev.filter(bid =>
                filteredBranches.some(b => b.id === bid)
            )
        );
    }, [selectedDivisionalAreas, districtData]);

    useEffect(() => {
        if (loginPlaceId === "1") {
            return;
        }

        const branchIds = availableBranches.map(b => b.id);
        if (branchIds.length === 0) {
            setBranchWasams([]);
            return;
        }

        let cancelled = false;
        getAllWasamsByBranchIds(branchIds)
            .then((res) => {
                if (cancelled) return;
                const list = res && res.data ? res.data : [];
                setBranchWasams(list.map(w => ({
                    wastId: w.wastId,
                    wasName: w.wasName,
                    wasCode: w.wasCode,
                })));
            })
            .catch(() => {
                if (!cancelled) setBranchWasams([]);
            });

        return () => { cancelled = true; };
    }, [availableBranches, loginPlaceId]);

    useEffect(() => {
        setSelectedWasam(prev =>
            prev.filter(id => wasamOptions.some(w => w.wastId === id))
        );
    }, [wasamOptions]);

    const isMemberProduct = (product) =>
        (product.productName || '').toUpperCase().includes('MEMBER');

    const savingsProducts = products.filter((product) => !isMemberProduct(product));
    const memberProducts  = products.filter((product) => isMemberProduct(product));

    const handleDateRangeChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    };

    const validateDates = () => {
        if (!startDate || !endDate) {
            message.warning('Please select both a Start Date and an End Date.');
            return false;
        }
        if (startDate.isAfter(endDate)) {
            message.error('Start Date must not be later than End Date.');
            return false;
        }
        return true;
    };

    const buildFilterLabels = () => {
        const filters = {};

        if (loginPlaceId === "3" || loginPlaceId === "4") {
            filters.districts = selectedDistricts.length > 0
                ? districtData
                    .filter(d => selectedDistricts.includes(d.districtId))
                    .map(d => d.districtName)
                    .join(', ')
                : 'All';
        }

        if (loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") {
            filters.divisionals = selectedDivisionalAreas.length > 0
                ? districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => selectedDivisionalAreas.includes(div.divAId))
                    .map(div => div.divAIdName)
                    .join(', ')
                : 'All';

            filters.branches = selectedBranches.length > 0
                ? districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .flatMap(div => div.branchDtoList || [])
                    .filter(b => selectedBranches.includes(b.id))
                    .map(b => b.branchName)
                    .join(', ')
                : 'All';
        }

        if (loginPlaceId === "1") {
            filters.branches = branchName || 'All';
        }

        if (wasamOptions.length > 0 || selectedWasam.length > 0) {
            filters.wasams = selectedWasam.length > 0
                ? wasamOptions
                    .filter(w => selectedWasam.includes(w.wastId))
                    .map(w => w.wasName)
                    .join(', ')
                : 'All';
        }

        return filters;
    };

    const handleGenerateReport = async () => {
        if (!validateDates()) return;

        setLoading(true);

        try {
            const payload = {
                fromDate: startDate.startOf('day').toDate().toISOString(),
                toDate:   endDate.endOf('day').toDate().toISOString(),
                productIdList: selectedProducts.length > 0
                    ? selectedProducts.map((id) => parseInt(id))
                    : null,
                districtIdList: selectedDistricts.length > 0 ? selectedDistricts : null,
                divAIdList: selectedDivisionalAreas.length > 0 ? selectedDivisionalAreas : null,
                branchIdList: loginPlaceId === "1"
                    ? [branchId]
                    : (selectedBranches.length > 0 ? selectedBranches : null),
                wasamIdList: selectedWasam.length > 0 ? selectedWasam : null,
            };
            const response = await getOpenAccountsReport(payload);
            const data = Array.isArray(response) ? response : [];

            if (data.length === 0) {
                message.warning('No accounts found for the selected criteria.');
                return;
            }

            await exportOpenAccountsExcelReport({
                data,
                startDate: startDate.format('YYYY-MM-DD'),
                endDate:   endDate.format('YYYY-MM-DD'),
                loginPlaceId,
                filters: buildFilterLabels(),
            });

            message.success(`Report generated successfully (${data.length} account(s)).`);
        } catch (err) {
            console.error('OpenAccountReport fetch error:', err);
            message.error('Failed to generate report: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const disabledDate = (current) => current && current > moment().endOf('day');

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>
                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selectedDistricts}
                                        onChange={setSelectedDistricts}
                                        maxTagCount="responsive"
                                        size="large"
                                    >
                                        {districtData
                                            .filter(d => loginPlaceId === "4" ? d.districtName === districtName : true)
                                            .map(d => (
                                                <Option key={d.districtId} value={d.districtId}>
                                                    🏙️ {d.districtName}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Divisional Areas">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select divisional areas"
                                        style={{ width: '100%' }}
                                        value={selectedDivisionalAreas}
                                        onChange={setSelectedDivisionalAreas}
                                        maxTagCount="responsive"
                                        size="large"
                                    >
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>
                                                🏘️ {d.divAIdName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Branch(es)">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select branches"
                                        style={{ width: '100%' }}
                                        value={selectedBranches}
                                        onChange={(values) => {
                                            if (values.includes('ALL')) {
                                                setSelectedBranches(availableBranches.map(b => b.id));
                                            } else {
                                                setSelectedBranches(values);
                                            }
                                        }}
                                        disabled={availableBranches.length === 0}
                                        maxTagCount="responsive"
                                        size="large"
                                    >
                                        <Option key="ALL" value="ALL">
                                            🔘 Select All Branches
                                        </Option>
                                        {availableBranches.map(branch => (
                                            <Option key={branch.id} value={branch.id}>
                                                🏢 {branch.branchName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.Item label="Wasam">
                                <Select
                                    mode="multiple"
                                    placeholder="Select wasam"
                                    style={{ width: '100%' }}
                                    value={selectedWasam}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setSelectedWasam(wasamOptions.map(w => w.wastId));
                                        } else {
                                            setSelectedWasam(values);
                                        }
                                    }}
                                    disabled={wasamOptions.length === 0}
                                    maxTagCount="responsive"
                                    size="large"
                                >
                                    <Option value="ALL">All Wasam</Option>
                                    {wasamOptions.map((wasam) => (
                                        <Option key={wasam.wastId} value={wasam.wastId}>
                                            🏙️ {wasam.wasName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Product">
                                <Select
                                    id="open-account-product-select"
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select Product(s)"
                                    style={{ width: '100%' }}
                                    value={selectedProducts}
                                    onChange={(values) => setSelectedProducts(values)}
                                    maxTagCount="responsive"
                                    size="large"
                                >
                                    {savingsProducts.length > 0 && (
                                        <OptGroup label="Savings">
                                            {savingsProducts.map((product) => (
                                                <Option key={product.productId} value={product.productId}>
                                                    {product.productName}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    )}
                                    {memberProducts.length > 0 && (
                                        <OptGroup label="Member Deposit">
                                            {memberProducts.map((product) => (
                                                <Option key={product.productId} value={product.productId}>
                                                    {product.productName}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Select Date Range" required>
                                <RangePicker
                                    id="open-account-date-range"
                                    placeholder={['Start Date', 'End Date']}
                                    style={{ width: '100%' }}
                                    value={startDate && endDate ? [startDate, endDate] : []}
                                    onChange={handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                    disabledDate={disabledDate}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    id="open-account-generate-btn"
                                    onClick={handleGenerateReport}
                                    disabled={loading || !startDate || !endDate}
                                    className="generate-btn"
                                    style={{ textAlign: 'right' }}
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default OpenAccountReport;
